// ───────────────────────────────────────────────────────────────────
// Theme tokens — each theme is a distinct visual language.
// Mirrors the 4 themes available in the PDF renderer (services/pdf.py).
// ───────────────────────────────────────────────────────────────────
export const THEMES = {
  mono: {
    page: "bg-[#0A0A0A] text-white",
    surface: "border-[#262626] bg-transparent",
    muted: "text-zinc-500",
    body: "text-zinc-300",
    rule: "border-[#262626]",
    accentRule: "border-[#262626]",
    headingFont: "font-display tracking-tighter",
    numberCls: "text-4xl",
    labelCls: "text-[10px] uppercase tracking-[0.28em] text-zinc-500",
    sectionLabel: "text-[10px] uppercase tracking-[0.22em] text-zinc-500",
    totalCls: "text-white text-2xl",
    payCardCls: "border border-[#262626]",
    addressCls: "bg-[#0A0A0A] border border-[#262626] text-zinc-200",
    inputCls: "bg-[#0A0A0A] border border-[#262626] focus:border-white",
    primaryBtn: "bg-white text-black hover:bg-zinc-200",
    ghostBtn: "border border-[#262626] hover:border-white text-zinc-300 hover:text-white",
    qrFrame: "bg-white p-3 rounded-md",
    notesCls: "text-zinc-400 border-l-2 border-[#262626] pl-4",
    footerCls: "text-zinc-600",
    bannerPaid: "border-emerald-500/30 bg-emerald-500/5 text-emerald-400",
    bannerPending: "border-amber-500/30 bg-amber-500/5 text-amber-400",
    sealRing: "rgba(255,255,255,0.55)",
    sealInk: "rgba(255,255,255,0.78)",
    bgAccent: false,
  },
  atelier: {
    page: "bg-[#0E0B08] text-[#f1e7d5]",
    surface: "border-[#3a2f23] bg-transparent",
    muted: "text-[#a89a82]",
    body: "text-[#e7d9c2]",
    rule: "border-[#3a2f23]",
    accentRule: "border-double border-b-[3px]",
    headingFont: "italic",
    numberCls: "text-4xl",
    labelCls: "text-[10px] uppercase tracking-[0.4em]",
    sectionLabel: "text-[9px] uppercase tracking-[0.3em] font-semibold",
    totalCls: "text-3xl italic",
    payCardCls: "border",
    addressCls: "bg-[#15110c] border text-[#f1e7d5]",
    inputCls: "bg-[#15110c] border focus:border-[#f1e7d5]",
    primaryBtn: "bg-[#f1e7d5] text-[#0E0B08] hover:opacity-90",
    ghostBtn: "border hover:opacity-90 text-[#e7d9c2]",
    qrFrame: "bg-[#f1e7d5] p-3",
    notesCls: "italic pl-4 border-l-2",
    footerCls: "italic",
    bannerPaid: "border bg-[#f1e7d5]/5",
    bannerPending: "border bg-amber-500/5 text-amber-300",
    sealRing: "rgba(241,231,213,0.6)",
    sealInk: "rgba(241,231,213,0.85)",
    fontFamily: "Georgia, 'Times New Roman', serif",
    bgAccent: true, // borders/dividers driven by brand color
  },
  manuscript: {
    page: "bg-[#FBF7EC] text-[#231d12]",
    surface: "border-[#d6cfb8] bg-[#FBF7EC]",
    muted: "text-[#8a7f63]",
    body: "text-[#3a3122]",
    rule: "border-[#d6cfb8]",
    accentRule: "border-b border-[#d6cfb8]",
    headingFont: "italic",
    numberCls: "text-4xl",
    labelCls: "text-[10px] uppercase tracking-[0.45em] text-[#8a7f63]",
    sectionLabel: "text-[9px] uppercase tracking-[0.35em] text-[#8a7f63] italic",
    totalCls: "text-3xl italic",
    payCardCls: "border border-[#d6cfb8] bg-[#f7f3e6]",
    addressCls: "bg-[#f7f3e6] border border-[#d6cfb8] text-[#231d12]",
    inputCls: "bg-[#f7f3e6] border border-[#d6cfb8] focus:border-[#231d12]",
    primaryBtn: "bg-[#231d12] text-[#FBF7EC] hover:opacity-90",
    ghostBtn: "border border-[#d6cfb8] hover:border-[#231d12] text-[#3a3122]",
    qrFrame: "bg-white p-3 border border-[#d6cfb8]",
    notesCls: "italic text-[#5a4f3b] bg-[#f7f3e6] border-l-[3px] pl-4 py-3",
    footerCls: "italic text-[#8a7f63]",
    bannerPaid: "border bg-[#f7f3e6]",
    bannerPending: "border border-amber-600/40 bg-amber-50 text-amber-700",
    sealRing: "rgba(35,29,18,0.5)",
    sealInk: "rgba(255,250,235,0.92)",
    fontFamily: "Garamond, Georgia, 'Times New Roman', serif",
    centeredHead: true,
    ornament: true,
    bgAccent: true,
  },
  modernist: {
    page: "bg-white text-[#0a0a0a]",
    surface: "border-[#e5e5e5] bg-white",
    muted: "text-[#777]",
    body: "text-[#1a1a1a]",
    rule: "border-[#e5e5e5]",
    accentRule: "",
    headingFont: "font-black tracking-[-0.04em] uppercase",
    numberCls: "text-5xl",
    labelCls: "text-[10px] uppercase tracking-[0.32em] font-bold",
    sectionLabel: "text-[9px] uppercase tracking-[0.28em] font-bold",
    totalCls: "text-3xl font-black uppercase tracking-tight",
    payCardCls: "border-l-[6px] bg-[#fafafa]",
    addressCls: "bg-white border border-[#e5e5e5] text-[#0a0a0a]",
    inputCls: "bg-white border border-[#e5e5e5] focus:border-[#0a0a0a]",
    primaryBtn: "bg-[#0a0a0a] text-white hover:bg-[#222]",
    ghostBtn: "border border-[#e5e5e5] hover:border-[#0a0a0a] text-[#1a1a1a]",
    qrFrame: "bg-white p-3 border border-[#e5e5e5]",
    notesCls: "bg-[#fafafa] border-l-4 pl-4 py-3 text-[#222]",
    footerCls: "font-bold uppercase tracking-[0.32em] text-[#777]",
    bannerPaid: "border-l-[6px] bg-[#0a0a0a] text-white",
    bannerPending: "border-l-[6px] bg-amber-500/10 text-amber-700",
    sealRing: "rgba(255,255,255,0.7)",
    sealInk: "rgba(255,255,255,0.92)",
    bigAccentBar: true,
    bgAccent: true,
  },
};

export const resolveTheme = (name) =>
  THEMES[(name || "mono").toLowerCase()] ? THEMES[(name || "mono").toLowerCase()] : THEMES.mono;

export const truncate = (s) => (s && s.length > 16 ? `${s.slice(0, 8)}…${s.slice(-6)}` : s);
